import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AppTheme } from "../../core/theme/app-theme";
import { readCachedAdminThemeLogo } from "../../core/theme/admin-theme-tokens-applier";
import { ThemeService } from "../../core/theme/theme.service";
import {
  BrandingService,
  emptyAdminBranding,
} from "../../core/branding/branding.service";
import { SaveAdminBrandingRequest } from "../../services/admin-branding.service";
import { AuthService, User } from "../../services/auth.service";
import { getStaffRoleDisplayName, isAdminRole } from "../../utils/user-roles";
import { MatMenuTrigger } from "@angular/material/menu";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";

@Component({
    selector: "app-navigation",
    templateUrl: "./navigation.component.html",
    styleUrls: ["./navigation.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavigationComponent implements OnInit, AfterViewInit, OnDestroy {
  logoSrc: string = readCachedAdminThemeLogo() ?? AppTheme.logoSrc;
  branding: SaveAdminBrandingRequest = emptyAdminBranding();
  currentUser: User | null = null;

  sidenavOpened = true;
  sidenavMode: "side" | "over" = "side";
  isMobileLayout = false;
  isSettingsRoute = false;
  currentPath = "";

  @ViewChild("userMenuTrigger") userMenuTrigger?: MatMenuTrigger;
  private readonly destroy$ = new Subject<void>();

  get logoAlt(): string {
    return `${this.branding.sidebarTitle} ${this.branding.sidebarSubtitle}`.trim();
  }

  navigationItems = [
    { path: "/dashboard", icon: "dashboard", label: "Dashboard", badge: null },
    { path: "/users", icon: "people", label: "Users", badge: null },
    {
      path: "/locations",
      icon: "location_on",
      label: "Locations",
      badge: null,
    },
    {
      path: "/payment-methods",
      icon: "credit_card",
      label: "Payment Methods",
      badge: null,
    },
    {
      path: "/serial-numbers",
      icon: "confirmation_number",
      label: "Serial Numbers",
      badge: null,
    },
    { path: "/courses", icon: "school", label: "Courses", badge: null, matchPrefix: true },
    {
      path: "/violations",
      icon: "gavel",
      label: "Respondents",
      badge: null,
      matchPrefix: true,
    },
    {
      path: "/enrollments",
      icon: "assignment",
      label: "Enrollments",
      badge: null,
    },
    { path: "/payments", icon: "payment", label: "Payments", badge: null },
    {
      path: "/class-management",
      icon: "event",
      label: "Class Attendance",
      badge: null,
      matchPrefix: true,
    },
    {
      path: "/course-completed",
      icon: "check_circle",
      label: "Course Completed",
      badge: null,
    },
    {
      path: "/communications",
      icon: "message",
      label: "Communications",
      badge: null,
      matchPrefix: true,
    },
    {
      path: "/reports",
      icon: "assessment",
      label: "Reports",
      badge: null,
      matchPrefix: true,
    },
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private themeService: ThemeService,
    private brandingService: BrandingService
  ) {
    this.branding = this.brandingService.getBranding();
  }


  ngOnInit(): void {
    this.themeService.logoUrl$.pipe(takeUntil(this.destroy$)).subscribe((url) => {
      this.logoSrc = url;
    });
    this.brandingService.branding$.pipe(takeUntil(this.destroy$)).subscribe((branding) => {
      this.branding = branding;
    });
    this.updateSidenavLayout();
    this.syncRouteState(this.router.url);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const navigationEnd = event as NavigationEnd;
        this.syncRouteState(navigationEnd.urlAfterRedirects);
      });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
      });
  }

  ngAfterViewInit(): void {
    // DevTools open/close triggers a resize; keep the menu anchored to the icon.
    fromEvent(window, "resize")
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateSidenavLayout();
        this.updateUserMenuPosition();
      });
  }

  private updateSidenavLayout(): void {
    const isMobileLayout =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches;

    if (isMobileLayout === this.isMobileLayout) return;

    this.isMobileLayout = isMobileLayout;
    this.sidenavMode = isMobileLayout ? "over" : "side";
    this.sidenavOpened = !isMobileLayout;
  }

  onUserMenuOpened(): void {
    // Initial open can be slightly mispositioned if layout is still settling.
    this.updateUserMenuPosition();
  }

  private updateUserMenuPosition(): void {
    if (!this.userMenuTrigger?.menuOpen) return;

    // Update immediately, then again on next frame, then after a short delay.
    // This handles DevTools panel toggles which can shift layout twice.
    this.userMenuTrigger.updatePosition();
    requestAnimationFrame(() => this.userMenuTrigger?.updatePosition());
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.userMenuTrigger?.updatePosition());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidenav(): void {
    this.sidenavOpened = !this.sidenavOpened;
  }

  onSidenavOpenedChange(opened: boolean): void {
    this.sidenavOpened = opened;
  }

  closeSidenavOnMobile(): void {
    if (this.isMobileLayout) {
      this.sidenavOpened = false;
    }
  }

  openSettings(): void {
    if (!this.canAccessSettings()) {
      return;
    }
    this.router.navigate(["/settings"]);
    this.closeSidenavOnMobile();
  }

  canAccessSettings(): boolean {
    return this.authService.isSuperAdmin();
  }

  onNavItemClick(event: MouseEvent, item: { path: string }): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    void this.router.navigateByUrl(item.path);
    this.closeSidenavOnMobile();
  }

  isSettingsActive(): boolean {
    return this.isSettingsRoute;
  }

  isNavItemActive(item: { path: string; matchPrefix?: boolean }): boolean {
    if (item.matchPrefix) {
      return (
        this.currentPath === item.path ||
        this.currentPath.startsWith(`${item.path}/`)
      );
    }
    return this.currentPath === item.path;
  }

  private syncRouteState(url: string): void {
    this.currentPath = (url || "").split("?")[0].split("#")[0];
    this.updateSettingsRouteState(url);
  }

  private updateSettingsRouteState(url: string): void {
    const path = (url || "").split("?")[0];
    this.isSettingsRoute = path === "/settings" || path.startsWith("/settings/");
  }

  logout(): void {
    this.authService.logout().subscribe();
    void this.router.navigate(["/login"]);
  }

  getUserDisplayName(): string {
    if (!this.currentUser) return "";
    return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
  }

  getUserInitials(): string {
    if (!this.currentUser) return "";
    const first = (this.currentUser.firstName || "").trim();
    const last = (this.currentUser.lastName || "").trim();
    const a = first ? first[0] : "";
    const b = last ? last[0] : "";
    const initials = (a + b).toUpperCase();
    return initials || (this.currentUser.email?.trim()?.[0]?.toUpperCase() ?? "");
  }

  getRoleLabel(): string {
    const role = this.currentUser?.role;
    if (!role) return "";
    const label = getStaffRoleDisplayName(role);
    if (isAdminRole(role)) return "System Administrator";
    return label || "Instructor";
  }

  downloadUserGuide(): void {
    this.router.navigate(['/user-guide']);
  }
}
